import {
  type PageInput,
  type QuestionSearchField,
  type QuestionStatus,
  type SearchParams,
  type SearchQuestionsByKeywordInput,
  ExportSearchResultsSourceFeature,
  useSearchQuestionsByKeywordLazyQuery,
} from '@exam-management/graphql';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FileDownloadOutlinedIcon,
  Flex,
  Paper,
  SearchRoundedIcon,
  SearchMultipleSelection,
  Text,
  TextField,
  type OptionSelectionProps,
  FindInPageOutlinedIcon,
  Loading,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useQBankLabels from '../../hooks/use-qbank-labels';
import useQBankOptions from '../../hooks/use-qbank-options';
import useSearchByKeywordsParams from '../../hooks/use-search-by-keywords-params';
import ExportSearchResultsModal from '../export-search-results-modal';

import {
  getStatusChipSx,
  KEYWORD_MIN_LENGTH,
  KEYWORD_MIN_LENGTH_ERROR,
  QUESTION_STATUS_OPTIONS,
  SEARCH_FIELD_OPTIONS,
  SELECTION_HEIGHT,
} from './constants';
import SearchPagination from './search-pagination';
import SearchResultCard from './search-result-card';
import { useResolvedSearchItems } from './use-resolved-search-items';

export default function SearchByKeywords() {
  const [
    {
      keyword: urlKeyword,
      qbankIds: urlQbankIds,
      status: urlStatus,
      fields: urlFields,
      page,
      pageSize,
    },
    setQueryParams,
  ] = useSearchByKeywordsParams();

  const {
    options: qbankOptions,
    isLoadingFirstPage: qbankLoading,
    isLoadingMore: isLoadingMoreQBanks,
    hasMore: hasMoreQBanks,
    open: openQbankOptions,
    search: searchQbankOptions,
    loadMore: loadMoreQBanks,
    refIdToCodeMap: qBankRefIdToCodeMap,
  } = useQBankOptions();
  // Read via ref inside a stable PaperComponent so toggling it doesn't remount the
  // dropdown (which would reset the scroll position and break infinite scroll).
  const isLoadingMoreRef = useRef(isLoadingMoreQBanks);
  isLoadingMoreRef.current = isLoadingMoreQBanks;
  const QBankLoadingPaper = useCallback(
    (paperProps: React.HTMLAttributes<HTMLElement>) => (
      <Paper {...paperProps}>
        {paperProps.children}
        {isLoadingMoreRef.current ? (
          <Flex sx={{ justifyContent: 'center', alignItems: 'center', minHeight: 48, py: 2 }}>
            <CircularProgress size={24} />
          </Flex>
        ) : null}
      </Paper>
    ),
    [],
  );

  const [keyword, setKeyword] = useState(urlKeyword);
  const [qbank, setQbank] = useState<OptionSelectionProps[]>(() =>
    urlQbankIds.map((id) => ({ label: id, value: id })),
  );
  const [qbankInput, setQbankInput] = useState('');
  const selectedQbankIds = useMemo(() => qbank.map((q) => q.value as string), [qbank]);
  const { data: qbankLabels } = useQBankLabels(selectedQbankIds);
  // Resolve chip labels from selection (q.label) or by-id lookup (qbankLabels) only —
  // deliberately NOT from the paginated search options, so a search fetch doesn't change
  // this array's identity and make Autocomplete wipe the typed term.
  const qbankValue = useMemo(
    () =>
      qbank
        .filter((q) => {
          const id = q.value as string;
          const resolved = qbankLabels[id];
          return !!resolved;
        })
        .map((q) => {
          const id = q.value as string;
          const resolved = qbankLabels[id];
          return { value: q.value, label: resolved ?? 'กำลังโหลด...' };
        }),
    [qbank, qbankLabels],
  );
  const [questionStatus, setQuestionStatus] = useState<OptionSelectionProps[]>(() =>
    QUESTION_STATUS_OPTIONS.filter((o) => urlStatus.includes(o.value as string)),
  );
  const [searchField, setSearchField] = useState<OptionSelectionProps[]>(() =>
    SEARCH_FIELD_OPTIONS.filter((o) => urlFields.includes(o.value as string)),
  );
  const [keywordError, setKeywordError] = useState('');

  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [hasSearched, setHasSearched] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { showSnackbar } = useSnackbar();

  const [searchQuestions, { data, loading, error }] = useSearchQuestionsByKeywordLazyQuery({
    onCompleted: () => {
      document.getElementById('frame-content')?.scrollTo({ top: 0 });
    },
    onError: () => {
      showSnackbar({
        variant: 'error',
        message: 'ไม่สามารถค้นหาข้อมูลได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
      });
    },
  });
  const lastInput = useRef<SearchQuestionsByKeywordInput | null>(null);

  const buildSearchParams = (input: SearchQuestionsByKeywordInput): SearchParams => ({
    keyword: input.keyword,
    qBankStructureCodes: (input.refQBankStructureIds ?? [])
      .map((id) => qBankRefIdToCodeMap[id])
      .filter((code): code is number => code !== undefined)
      .sort((a, b) => a - b)
      .map(String),
    questionStatuses: input.status ?? [],
    matchFields: input.fields ?? [],
  });

  const runSearch = (input: SearchQuestionsByKeywordInput | null, paging: PageInput) => {
    if (!input) return;
    searchQuestions({ variables: { input, paging }, fetchPolicy: 'network-only' });
  };

  const handleChangeKeyword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);

    if (keywordError) {
      setKeywordError('');
    }
  };

  const handleSearch = () => {
    if (keyword.trim().length < KEYWORD_MIN_LENGTH) {
      setKeywordError(KEYWORD_MIN_LENGTH_ERROR);
      return;
    }

    const input: SearchQuestionsByKeywordInput = {
      keyword: keyword.trim(),
      refQBankStructureIds: qbank.length > 0 ? qbank.map((q) => q.value as string) : undefined,
      status:
        questionStatus.length > 0
          ? questionStatus.map((s) => s.value as QuestionStatus)
          : undefined,
      fields:
        searchField.length > 0 ? searchField.map((f) => f.value as QuestionSearchField) : undefined,
    };

    lastInput.current = input;
    setSearchParams(buildSearchParams(input));
    setKeywordError('');
    setHasSearched(true);
    setQueryParams({
      keyword: keyword.trim(),
      qbankIds: qbank.map((q) => q.value as string),
      status: questionStatus.map((s) => s.value as string),
      fields: searchField.map((f) => f.value as string),
      page: 0,
    });
    runSearch(input, { currentPage: 0, pageSize });
  };

  const handlePageChange = (newPage: number) => {
    setQueryParams({ page: newPage });
    runSearch(lastInput.current, { currentPage: newPage, pageSize });
  };

  const handleRowsPerPageChange = (newSize: number) => {
    setQueryParams({ pageSize: newSize, page: 0 });
    runSearch(lastInput.current, { currentPage: 0, pageSize: newSize });
  };

  const handleReset = () => {
    setKeyword('');
    setQbank([]);
    setQuestionStatus([]);
    setSearchField([]);
    setKeywordError('');
    setHasSearched(false);
    setSearchParams(undefined);
    setQueryParams({ keyword: '', qbankIds: [], status: [], fields: [], page: 0, pageSize: 20 });
    lastInput.current = null;
  };

  useEffect(() => {
    if (urlKeyword.trim().length >= KEYWORD_MIN_LENGTH) {
      const input: SearchQuestionsByKeywordInput = {
        keyword: urlKeyword.trim(),
        refQBankStructureIds: urlQbankIds.length > 0 ? urlQbankIds : undefined,
        status: urlStatus.length > 0 ? (urlStatus as QuestionStatus[]) : undefined,
        fields: urlFields.length > 0 ? (urlFields as QuestionSearchField[]) : undefined,
      };
      lastInput.current = input;
      setSearchParams(buildSearchParams(input));
      setHasSearched(true);
      runSearch(input, { currentPage: page, pageSize });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { items: pageItems, loading: resolvingImages } = useResolvedSearchItems(
    data?.qctSearchQuestionsByKeyword.items,
  );
  const total = data?.qctSearchQuestionsByKeyword.total ?? 0;

  return (
    <Paper
      sx={(theme) => ({
        width: '100%',
        minHeight: 'calc(100dvh - 181px)',
        borderRadius: theme.shape.rounded(2),
        overflow: 'hidden',
      })}
    >
      <Flex sx={{ flexDirection: 'column' }}>
        <Box
          sx={(theme) => ({
            px: theme.spacing(6),
            py: theme.spacing(5),
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          })}
        >
          <Text variant='h3'>ค้นหาด้วย Keyword</Text>
        </Box>

        <Flex
          sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6), p: theme.spacing(6) })}
        >
          <Box
            sx={(theme) => ({
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: theme.shape.rounded(2),
              p: theme.spacing(4),
            })}
          >
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
              <TextField
                value={keyword}
                onChange={handleChangeKeyword}
                error={!!keywordError}
                helperText={keywordError}
                placeholder='ระบุคำที่ต้องการค้นหา เช่น ตลาดทุน หรือ "กองทุนรวม"'
                slotProps={{
                  input: {
                    startAdornment: (
                      <SearchRoundedIcon sx={{ color: (theme) => theme.palette.grey[600] }} />
                    ),
                  },
                }}
                fullWidth
              />

              <Flex
                sx={(theme) => ({
                  gap: theme.spacing(4),
                  flexDirection: { xs: 'column', md: 'row' },
                })}
              >
                <Flex
                  sx={(theme) => ({
                    flexDirection: 'column',
                    gap: theme.spacing(1),
                    flex: 1,
                    minWidth: 0,
                  })}
                >
                  <Text variant='body2Semibold'>คลังข้อสอบ</Text>
                  <SearchMultipleSelection
                    value={qbankValue}
                    onChange={(_, newValue, reason) => {
                      setQbank(newValue);
                      if ((reason === 'selectOption' || reason === 'removeOption') && qbankInput) {
                        setQbankInput('');
                        searchQbankOptions('');
                      }
                    }}
                    options={qbankOptions}
                    loading={qbankLoading}
                    slots={{
                      paper: QBankLoadingPaper,
                    }}
                    slotProps={{
                      listbox: {
                        onScroll: (event) => {
                          const el = event.currentTarget;
                          if (
                            hasMoreQBanks &&
                            el.scrollHeight - el.scrollTop - el.clientHeight < 40
                          ) {
                            loadMoreQBanks();
                          }
                        },
                      },
                    }}
                    onOpen={openQbankOptions}
                    inputValue={qbankInput}
                    onInputChange={(_, value, reason) => {
                      // control the input so the term survives option updates after a fetch;
                      // ignore MUI's internal 'reset' (fired when value/options change)
                      if (reason === 'input' || reason === 'clear') {
                        setQbankInput(value);
                        searchQbankOptions(value);
                      }
                    }}
                    filterOptions={(opts) => opts}
                    limitTags={1}
                    placeholder={qbank.length === 0 ? 'เลือกคลังข้อสอบ' : ''}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: SELECTION_HEIGHT,
                        flexWrap: 'wrap',
                        pl: '14px',
                      },
                      '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                      },
                      '& .MuiChip-root': {
                        maxWidth: 200,
                        '& .MuiChip-label': {
                          fontSize: '12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                        '& .MuiChip-deleteIcon': { fontSize: 16 },
                      },
                    }}
                  />
                </Flex>
                <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1), flex: 1 })}>
                  <Text variant='body2Semibold'>สถานะคำถาม</Text>
                  <SearchMultipleSelection
                    value={questionStatus}
                    onChange={(_, newValue) => setQuestionStatus(newValue)}
                    options={QUESTION_STATUS_OPTIONS}
                    placeholder={questionStatus.length === 0 ? 'เลือกสถานะคำถาม' : ''}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: SELECTION_HEIGHT,
                        flexWrap: 'wrap',
                        pl: '14px',
                      },
                      '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                      },
                    }}
                    renderValue={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={`${option.value}-chip`}
                          label={option.label}
                          sx={getStatusChipSx(option.value as string)}
                        />
                      ))
                    }
                  />
                </Flex>
                <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1), flex: 1 })}>
                  <Text variant='body2Semibold'>ส่วนที่พบคำ</Text>
                  <SearchMultipleSelection
                    value={searchField}
                    onChange={(_, newValue) => setSearchField(newValue)}
                    options={SEARCH_FIELD_OPTIONS}
                    placeholder={searchField.length === 0 ? 'เลือกส่วนที่พบคำ' : ''}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: SELECTION_HEIGHT,
                        flexWrap: 'wrap',
                        pl: '14px',
                      },
                      '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                      },
                      '& .MuiChip-root': {
                        height: 'auto',
                        py: '12px',
                        pr: 1,
                        pl: 2,
                        lineHeight: '18px',
                        '& .MuiChip-label': { fontSize: '12px', lineHeight: '18px' },
                        '& .MuiChip-deleteIcon': { fontSize: 16 },
                      },
                    }}
                  />
                </Flex>
              </Flex>

              <Divider />

              <Flex
                sx={(theme) => ({ justifyContent: 'flex-end', gap: theme.spacing(2), height: 36 })}
              >
                <Button variant='outlined' color='secondary' onClick={handleReset}>
                  Reset
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<SearchRoundedIcon />}
                  onClick={handleSearch}
                >
                  ค้นหา
                </Button>
              </Flex>
            </Flex>
          </Box>

          {!hasSearched || (hasSearched && !loading && error) ? (
            <Flex
              sx={(theme) => ({
                minHeight: 348,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                gap: theme.spacing(3),
                textAlign: 'center',
              })}
            >
              <SearchRoundedIcon sx={{ color: 'grey.500', fontSize: 96 }} />
              <Text variant='h3'>ค้นหาข้อสอบด้วย Keyword</Text>
              <Text>พิมพ์คำที่ต้องการแล้วกดค้นหา</Text>
            </Flex>
          ) : null}

          {hasSearched && !loading && !error && total === 0 ? (
            <Flex
              sx={(theme) => ({
                minHeight: 348,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                gap: theme.spacing(3),
                textAlign: 'center',
              })}
            >
              <FindInPageOutlinedIcon sx={{ color: 'grey.400', fontSize: 96 }} />
              <Text variant='h3'>ไม่พบข้อสอบที่ตรงกับคำค้นหา</Text>
              <Text>ลองเปลี่ยนคำค้นหา หรือปรับ Filter ที่ใช้อยู่</Text>
            </Flex>
          ) : null}

          {hasSearched && !loading && !resolvingImages && !error && total > 0 ? (
            <>
              <Flex
                sx={(theme) => ({
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='h3'>พบ {total} รายการ</Text>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={() => setIsExportModalOpen(true)}
                >
                  Export หน้านี้ ({pageItems.length} รายการ)
                </Button>
              </Flex>

              <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
                {pageItems.map((item) => (
                  <SearchResultCard key={item.id} item={item} />
                ))}
              </Flex>

              <SearchPagination
                total={total}
                page={page}
                rowsPerPage={pageSize}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </>
          ) : null}

          {hasSearched && (loading || resolvingImages) ? (
            <Flex
              sx={(theme) => ({
                minHeight: 348,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                gap: theme.spacing(3),
              })}
            >
              <Loading />
            </Flex>
          ) : null}
        </Flex>
      </Flex>

      <ExportSearchResultsModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        questionIds={pageItems.map((item) => item.id)}
        sourceFeature={ExportSearchResultsSourceFeature.SearchByKeyword}
        searchParams={searchParams}
        total={pageItems.length}
        page={page}
        pageSize={pageSize}
      />
    </Paper>
  );
}
